import { useEffect } from "react";

type SeoInput = {
  title: string;
  description: string;
  /** Absolute or root-relative path. Defaults to current window.location. */
  canonical?: string;
  /** Absolute URL or path under /. Defaults to /logo/AAMECC ICON.png. */
  image?: string;
  /** Open Graph type. */
  type?: "website" | "article" | "product" | "profile";
  /** Comma-separated keywords (used sparingly — Google ignores, but Bing still reads). */
  keywords?: string;
  /** Optional structured data (JSON-LD). Stringified into a <script> tag. */
  jsonLd?: object | object[];
  /** Hide from indexing when true (e.g. dashboard pages). */
  noindex?: boolean;
};

const SITE_NAME = "AAMECC Digital Trade Chamber";
const SITE_URL = "https://aameccdigital.com";
const DEFAULT_IMAGE = `${SITE_URL}/logo/AAMECC%20ICON.png`;

const ensureMeta = (selector: string, attrs: Record<string, string>) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
  }
  return el;
};

const setMetaByName = (name: string, content: string) =>
  ensureMeta(`meta[name="${name}"]`, { name, content });

const setMetaByProperty = (property: string, content: string) =>
  ensureMeta(`meta[property="${property}"]`, { property, content });

const setLink = (rel: string, href: string) => {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
  return link;
};

const setJsonLd = (data: object | object[], id = "page-jsonld") => {
  let tag = document.getElementById(id) as HTMLScriptElement | null;
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
};

const removeJsonLd = (id = "page-jsonld") => {
  const tag = document.getElementById(id);
  if (tag) tag.remove();
};

const toAbsolute = (urlOrPath: string) => {
  if (!urlOrPath) return urlOrPath;
  if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;
  const path = urlOrPath.startsWith("/") ? urlOrPath : `/${urlOrPath}`;
  return `${SITE_URL}${path}`;
};

export function useSeo({
  title, description, canonical, image, type = "website",
  keywords, jsonLd, noindex,
}: SeoInput) {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    document.title = fullTitle;

    setMetaByName("description", description);
    if (keywords) setMetaByName("keywords", keywords);
    setMetaByName("robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");

    const href = toAbsolute(canonical || window.location.pathname + window.location.search);
    setLink("canonical", href);

    const imgUrl = image ? toAbsolute(image) : DEFAULT_IMAGE;

    // Open Graph
    setMetaByProperty("og:title", fullTitle);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:type", type);
    setMetaByProperty("og:url", href);
    setMetaByProperty("og:image", imgUrl);
    setMetaByProperty("og:site_name", SITE_NAME);
    setMetaByProperty("og:locale", "en_US");

    // Twitter
    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", fullTitle);
    setMetaByName("twitter:description", description);
    setMetaByName("twitter:image", imgUrl);
    setMetaByName("twitter:site", "@AAMECC");

    if (jsonLd) setJsonLd(jsonLd);
    else removeJsonLd();

    return () => {
      // Leave previous tags in place — next page's useSeo will overwrite.
      // Only the JSON-LD script is page-specific and worth clearing.
      removeJsonLd();
    };
  }, [title, description, canonical, image, type, keywords, JSON.stringify(jsonLd), noindex]);
}

export const SITE = { name: SITE_NAME, url: SITE_URL };
